import React from 'react';
import {
  FaArrowLeft,
  FaExclamationTriangle,
  FaHeart,
} from 'react-icons/fa';
import MediaUsageMeter from './MediaUsageMeter';
import ThemeSelector from './ThemeSelector';
import GiftEditorOverview from './giftEditor/GiftEditorOverview';
import ChapterListStep from './giftEditor/ChapterListStep';
import GiftTypeStep from './giftEditor/GiftTypeStep';
import SealStep from './giftEditor/SealStep';
import SettingsStep from './giftEditor/SettingsStep';
import SharingPrivacyStep from './giftEditor/SharingPrivacyStep';
import StoryCheckStep from './giftEditor/StoryCheckStep';
import StoryCoachStep from './giftEditor/StoryCoachStep';
import TemplateStep from './giftEditor/TemplateStep';

const UploadStatusBanner = ({ uploadStatus, theme }) => {
  const uploadActive = Boolean(uploadStatus?.active);
  const uploadHasIssue = Boolean(uploadStatus?.error);

  if (!uploadActive && !uploadHasIssue) return null;
  if (uploadActive && !uploadHasIssue) return null;

  return (
    <section className={`lumirae-editor-panel border p-4 ${uploadHasIssue ? 'bg-red-50/70' : ''}`}>
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm"
          style={{ color: uploadHasIssue ? 'var(--lumirae-state-error)' : theme.accent }}
        >
          <FaExclamationTriangle />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-black uppercase tracking-[0.24em]" style={{ color: uploadHasIssue ? 'var(--lumirae-state-error)' : theme.accent }}>
            Upload needs attention
          </div>
          <p className="mt-1 truncate text-xs font-bold opacity-60">
            {uploadStatus.error}
          </p>
        </div>
      </div>
    </section>
  );
};

const PANEL_TITLES = {
  overview: 'Choose what to edit',
  theme: 'Theme',
  cover: 'Opening Cover',
  modules: 'Chapters',
  privacy: 'Share',
  publish: 'Publish',
  settings: 'Settings',
};

const GiftEditorHero = ({
  theme,
  activePanel,
  focusedModuleId,
  focusedModuleIndex,
  focusedModuleCount,
  onDoneFocus,
  onOpenOverview,
}) => {
  const canReturn = Boolean(focusedModuleId || activePanel !== 'overview');

  return (
    <div id="gift-builder-start" className="scroll-mt-6 lumirae-builder-mobile-top">
      <button
        type="button"
        className={`lumirae-builder-menu-button ${canReturn ? 'is-back' : ''}`}
        aria-label={canReturn ? 'Back to editor overview' : 'Editor overview'}
        onClick={canReturn ? (focusedModuleId ? onDoneFocus : onOpenOverview) : onOpenOverview}
      >
        {canReturn ? (
          <>
            <FaArrowLeft />
            <em>Back</em>
          </>
        ) : (
          <>
            <span />
            <span />
          </>
        )}
      </button>
      <div className="text-center">
        <div className="lumirae-builder-brand">Lumirae</div>
        <div className="mt-2 text-[10px] font-medium tracking-tight" style={{ color: theme.text }}>
          {focusedModuleId ? 'Edit Chapter' : PANEL_TITLES[activePanel] || 'Gift Builder'}
        </div>
      </div>
      <div className="lumirae-builder-status-slot">
        <FaHeart className="ml-auto text-sm" style={{ color: theme.accent }} />
        <div className="lumirae-builder-chapter-pill" style={{ color: theme.accent }}>
          {focusedModuleId ? `Chapter ${Math.max(1, focusedModuleIndex + 1)} of ${Math.max(1, focusedModuleCount)}` : 'Editor'}
        </div>
      </div>
    </div>
  );
};

const GiftEditor = ({
  pageData,
  setPageData,
  isLocked,
  theme,
  storyAnalysis,
  onSealGift,
  onPreviewGift,
  onPresetChange,
  onThemeSelect,
  onStoryCoachAnswer,
  onApplyStoryCoach,
  onApplyAllStoryCoach,
  onAddModule,
  onSharingModeChange,
  uploadStatus,
  focusedModuleId,
  onFocusModule,
  focusedModuleIndex = -1,
  focusedModuleCount = 0,
  onFocusPrevious,
  onFocusNext,
  onDoneFocus,
  onUpdateModuleConfig,
  onRemoveModule,
  onMoveModule,
  activePanel = 'overview',
  onPanelChange,
  giftIntroContent,
  onSaveDraft,
  onChangePassword,
  onDeleteGift,
  onGiftSetSettings,
  onOpenPrivacy,
}) => {
  const uploadActive = Boolean(uploadStatus?.active);

  const handlePanelChange = (panelId) => {
    if (panelId !== 'modules') {
      onDoneFocus?.();
    }
    onPanelChange?.(panelId);
    window.setTimeout(() => {
      document.getElementById('gift-builder-start')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 40);
  };

  const shownPanel = focusedModuleId ? 'modules' : activePanel;

  return (
    <div className="space-y-5">
      <GiftEditorHero
        theme={theme}
        activePanel={shownPanel}
        focusedModuleId={focusedModuleId}
        focusedModuleIndex={focusedModuleIndex}
        focusedModuleCount={focusedModuleCount}
        onDoneFocus={onDoneFocus}
        onOpenOverview={() => handlePanelChange('overview')}
      />

      <UploadStatusBanner uploadStatus={uploadStatus} theme={theme} />

      {!focusedModuleId && shownPanel === 'overview' && (
        <GiftEditorOverview
          pageData={pageData}
          theme={theme}
          isLocked={isLocked}
          onPanelChange={handlePanelChange}
        />
      )}

      {!focusedModuleId && shownPanel === 'theme' && (
        <>
          <TemplateStep
            pageData={pageData}
            theme={theme}
            onPresetChange={onPresetChange}
            onAddModule={onAddModule}
          />
          <ThemeSelector
            currentSelectedThemeId={pageData.themeId}
            onThemeSelect={onThemeSelect}
          />
          <GiftTypeStep pageData={pageData} setPageData={setPageData} theme={theme} />
        </>
      )}

      {!focusedModuleId && shownPanel === 'cover' && giftIntroContent}

      {shownPanel === 'modules' && (
        <>
          <ChapterListStep
            pageData={pageData}
            theme={theme}
            focusedModuleId={focusedModuleId}
            onFocusModule={onFocusModule}
            focusedModuleIndex={focusedModuleIndex}
            focusedModuleCount={focusedModuleCount}
            onFocusPrevious={onFocusPrevious}
            onFocusNext={onFocusNext}
            onDoneFocus={onDoneFocus}
            onUpdateModuleConfig={onUpdateModuleConfig}
            onAddModule={onAddModule}
            onRemoveModule={onRemoveModule}
            onMoveModule={onMoveModule}
            forceOpen
          />
          {!focusedModuleId && (
            <section id="gift-builder-media" className="scroll-mt-6">
              <MediaUsageMeter pageData={pageData} theme={theme} />
            </section>
          )}
        </>
      )}

      {!focusedModuleId && shownPanel === 'privacy' && (
        <SharingPrivacyStep
          pageData={pageData}
          theme={theme}
          onSharingModeChange={onSharingModeChange}
        />
      )}

      {!focusedModuleId && shownPanel === 'publish' && (
        <>
          <StoryCheckStep
            storyAnalysis={storyAnalysis}
            theme={theme}
            uploadActive={uploadActive}
            isLocked={isLocked}
            onPreviewGift={onPreviewGift}
            onSealGift={onSealGift}
          />
          <StoryCoachStep
            pageData={pageData}
            theme={theme}
            onStoryCoachAnswer={onStoryCoachAnswer}
            onApplyStoryCoach={onApplyStoryCoach}
            onApplyAllStoryCoach={onApplyAllStoryCoach}
            onAddModule={onAddModule}
          />
          <SealStep
            theme={theme}
            isLocked={isLocked}
            uploadActive={uploadActive}
            onSaveDraft={onSaveDraft}
            onPreviewGift={onPreviewGift}
            onSealGift={onSealGift}
          />
        </>
      )}

      {!focusedModuleId && shownPanel === 'settings' && (
        <SettingsStep
          theme={theme}
          onChangePassword={onChangePassword}
          onGiftSetSettings={onGiftSetSettings}
          onOpenPrivacy={onOpenPrivacy}
          onDeleteGift={onDeleteGift}
        />
      )}

    </div>
  );
};

export default GiftEditor;
